import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, LogOut, User, ChevronDown, ShieldCheck, Shield, Calendar, Settings, FileText, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import logo from "@/assets/logo-transparent.png";

interface NavItem {
  to: string;
  label: string;
  children?: { to: string; label: string }[];
  hover?: boolean;
}

const baseLinks = (signedIn: boolean): NavItem[] => [
  ...(signedIn ? [] : [{ to: "/hwhc-therapy", label: "Client Services" }]),
  { to: "/for-practitioners", label: "For Practitioners" },
  {
    to: "/resources",
    label: "Resources",
    children: [
      { to: "/resources", label: "Blog" },
      { to: "/events", label: "Event Calendar" },
    ],
  },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { user, signOut } = useAuth();

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin-nav", user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  const links: NavItem[] = baseLinks(!!user);

  // Scroll detection for glassmorphism transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      ref={navRef}
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass border-b border-border/30 shadow-sm"
          : "bg-background/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img src={logo} alt="HW Healing Center logo" className="h-[50px] w-[50px] object-contain" />
          <span className="text-base font-semibold tracking-tight text-foreground">Hope & Wellness Healing Center</span>
        </Link>

        {/* Desktop nav */}
        <nav className="ml-auto hidden items-center gap-1 lg:flex">
          {links.map((l) =>
            l.children ? (
              l.hover ? (
                <div key={l.label} className="relative group">
                  <div className="flex items-center">
                    <Link
                      to={l.to}
                      className={`px-3 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                        l.to === location.pathname || l.children!.some((c) => c.to === location.pathname)
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {l.label}
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === l.label ? null : l.label);
                      }}
                      className={`px-1 py-2 text-sm transition-colors hover:text-foreground ${
                        l.to === location.pathname || l.children!.some((c) => c.to === location.pathname)
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                      aria-label={`Toggle ${l.label} menu`}
                    >
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform group-hover:rotate-180 ${openDropdown === l.label ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  <div className="absolute left-0 top-full hidden w-48 pt-1 group-hover:block z-50">
                    <div className="rounded-lg dropdown-panel py-1">
                      {l.children!.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`block px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${
                            location.pathname === child.to ? "font-medium text-foreground" : "text-foreground/90"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div key={l.label} className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === l.label ? null : l.label)}
                    className={`flex items-center gap-0.5 px-3 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                      l.to === location.pathname || l.children!.some((c) => c.to === location.pathname)
                        ? "text-foreground"
                        : "text-muted-foreground"
                    }`}
                  >
                    {l.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openDropdown === l.label ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === l.label && (
                    <div className="absolute left-0 top-full mt-1 w-48 rounded-lg dropdown-panel py-1 z-50">
                      {l.children!.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`block px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${
                            location.pathname === child.to ? "font-medium text-foreground" : "text-foreground/90"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                  location.pathname === l.to ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex ml-3">
          {user ? (
            <>
              {isAdmin && (
                <div className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === "Admin" ? null : "Admin")}
                    className={`flex items-center gap-0.5 px-3 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                      ["/admin/approvals", "/admin/google-calendar", "/admin/space-requests", "/admin/applications", "/admin/audit-log"].includes(location.pathname)
                        ? "text-foreground"
                        : "text-muted-foreground"
                    }`}
                  >
                    <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                    Admin
                    <ChevronDown className={`ml-0.5 h-3.5 w-3.5 transition-transform ${openDropdown === "Admin" ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === "Admin" && (
                    <div className="absolute right-0 top-full mt-1 w-48 rounded-lg dropdown-panel py-1 z-50">
                      <Link to="/admin/approvals" onClick={() => setOpenDropdown(null)} className={`block flex items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${location.pathname === "/admin/approvals" ? "font-medium text-foreground" : "text-foreground/90"}`}>
                        <ShieldCheck className="h-3.5 w-3.5" /> Approvals
                      </Link>
                      <Link to="/admin/google-calendar" onClick={() => setOpenDropdown(null)} className={`block flex items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${location.pathname === "/admin/google-calendar" ? "font-medium text-foreground" : "text-foreground/90"}`}>
                        <Calendar className="h-3.5 w-3.5" /> Calendar
                      </Link>
                      <Link to="/admin/space-requests" onClick={() => setOpenDropdown(null)} className={`block flex items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${location.pathname === "/admin/space-requests" ? "font-medium text-foreground" : "text-foreground/90"}`}>
                        <User className="h-3.5 w-3.5" /> Space Requests
                      </Link>
                      <Link to="/admin/applications" onClick={() => setOpenDropdown(null)} className={`block flex items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${location.pathname === "/admin/applications" ? "font-medium text-foreground" : "text-foreground/90"}`}>
                        <FileText className="h-3.5 w-3.5" /> Applications
                      </Link>
                      <Link to="/admin/audit-log" onClick={() => setOpenDropdown(null)} className={`block flex items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-accent/10 ${location.pathname === "/admin/audit-log" ? "font-medium text-foreground" : "text-foreground/90"}`}>
                        <Shield className="h-3.5 w-3.5" /> Audit Log
                      </Link>
                    </div>
                  )}
                </div>
              )}
              <Button asChild variant="ghost" size="sm" className="rounded-full">
                <Link to="/my-bookings">
                  <User className="mr-1 h-3.5 w-3.5" /> My Bookings
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="rounded-full">
                <Link to="/referral-directory">
                  <Users className="mr-1 h-3.5 w-3.5" /> Directory
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="rounded-full">
                <Link to="/account-settings">
                  <Settings className="mr-1 h-3.5 w-3.5" /> Settings
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="rounded-full glass border-border/50" onClick={signOut}>
                <LogOut className="mr-1 h-3.5 w-3.5" /> Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="outline" size="sm" className="rounded-full glass border-border/50">
                <Link to="/auth">Sign In</Link>
              </Button>
              <Button asChild size="sm" className="rounded-full gradient-accent text-accent-foreground shadow-md shadow-accent/20 hover:shadow-accent/35 transition-shadow">
                <Link to="/contact-us">Contact Us <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav className="border-t border-border/60 bg-background/95 backdrop-blur-md px-4 pb-4 lg:hidden">
          {links.map((l) =>
            l.children ? (
              l.hover ? (
                <div key={l.label}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={`block px-3 py-2.5 text-sm font-medium transition-colors hover:text-foreground ${
                        l.to === location.pathname || l.children!.some((c) => c.to === location.pathname)
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {l.label}
                    </Link>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === l.label ? null : l.label)}
                      className="px-3 py-2.5 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={`Toggle ${l.label} menu`}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === l.label ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {openDropdown === l.label && (
                    <div className="pb-1">
                      {l.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => setOpen(false)}
                          className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                            location.pathname === child.to ? "text-foreground" : "text-foreground/90"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div key={l.label}>
                  <span className="block px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    {l.label}
                  </span>
                  {l.children.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to}
                      onClick={() => setOpen(false)}
                      className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                        location.pathname === child.to ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )
            ) : (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`block px-3 py-2.5 text-sm font-medium transition-colors hover:text-foreground ${
                  location.pathname === l.to ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
          {user && isAdmin && (
            <div className="mt-2 border-t border-border/30 pt-2">
              <span className="block px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> Admin
              </span>
              <Link to="/admin/approvals" onClick={() => setOpen(false)} className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${location.pathname === "/admin/approvals" ? "text-foreground" : "text-muted-foreground"}`}>
                Approvals
              </Link>
              <Link to="/admin/google-calendar" onClick={() => setOpen(false)} className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${location.pathname === "/admin/google-calendar" ? "text-foreground" : "text-muted-foreground"}`}>
                Calendar
              </Link>
              <Link to="/admin/space-requests" onClick={() => setOpen(false)} className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${location.pathname === "/admin/space-requests" ? "text-foreground" : "text-muted-foreground"}`}>
                Space Requests
              </Link>
              <Link to="/admin/applications" onClick={() => setOpen(false)} className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${location.pathname === "/admin/applications" ? "text-foreground" : "text-muted-foreground"}`}>
                Applications
              </Link>
              <Link to="/admin/audit-log" onClick={() => setOpen(false)} className={`block px-6 py-2 text-sm font-medium transition-colors hover:text-foreground ${location.pathname === "/admin/audit-log" ? "text-foreground" : "text-muted-foreground"}`}>
                Audit Log
              </Link>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {user ? (
              <>
                <Button asChild size="sm" variant="ghost" className="flex-1 rounded-full">
                  <Link to="/my-bookings" onClick={() => setOpen(false)}>
                    <User className="mr-1 h-3.5 w-3.5" /> Bookings
                  </Link>
                </Button>
                <Button asChild size="sm" variant="ghost" className="flex-1 rounded-full">
                  <Link to="/referral-directory" onClick={() => setOpen(false)}>
                    <Users className="mr-1 h-3.5 w-3.5" /> Directory
                  </Link>
                </Button>
                <Button asChild size="sm" variant="ghost" className="flex-1 rounded-full">
                  <Link to="/account-settings" onClick={() => setOpen(false)}>
                    <Settings className="mr-1 h-3.5 w-3.5" /> Settings
                  </Link>
                </Button>
                <Button size="sm" variant="outline" className="flex-1 rounded-full glass border-border/50" onClick={() => { signOut(); setOpen(false); }}>
                  <LogOut className="mr-1 h-3.5 w-3.5" /> Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button asChild size="sm" variant="outline" className="flex-1 rounded-full glass border-border/50">
                  <Link to="/auth" onClick={() => setOpen(false)}>Sign In</Link>
                </Button>
                <Button asChild size="sm" className="flex-1 rounded-full gradient-accent text-accent-foreground">
                  <Link to="/contact-us" onClick={() => setOpen(false)}>Contact Us</Link>
                </Button>
              </>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
